import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { UserContext } from './UserContext';

// Create Context
const DBPostsContext = createContext();

export const useDBPosts = () => useContext(DBPostsContext);

export const DBPostsProvider = ({ children }) => {
  const [posts, setPosts] = useState([]);
  const { userInfo } = useContext(UserContext);
  const[isLoading, setIsLoading] = useState(false)
 // Fetch all posts
  // Fetch all posts
  const fetchPosts = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get(`${process.env.REACT_APP_API_END_POINT}/blog/post`);
      const fetchedPosts = response.data;
     
      // Conditionally exclude `content` field based on post visibility and user role
      const filteredPosts = fetchedPosts.map(post => {
        // If the post is not public and the user does not have a specific role, exclude content
        if (post.visability !== 'public' && (!userInfo || !userInfo.role)) {

          const { content, ...postWithoutContent } = post;
          return postWithoutContent;
        }
        return post; // Include the full post if it's public or the user has a role
      });

      setPosts(filteredPosts);
      setIsLoading(false)
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };


  // Add a new post
  const addPost = async (formData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_END_POINT}/blog/post`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true, // Add this to include cookies in the request
      });
      setPosts([response.data, ...posts]);
    } catch (error) {
      console.error('Error adding post:', error);
    }
  };
  

  // Update post
  const updatePost = async (postId, formData) => {
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_END_POINT}/blog/post/${postId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setPosts(posts.map(post => (post._id === postId ? response.data : post)));
    } catch (error) {
      console.error('Error updating post:', error);
    }
  };

  // Delete post
  const deletePost = async (postId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_END_POINT}/blog/post/${postId}`);
      setPosts(posts.filter(post => post._id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

// useDBPosts.js
const toggleFavoritePost = async (postId) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_END_POINT}/blog/post/${postId}/favorite`,
      {},
      { withCredentials: true }
    );


      

    // Get the updated post data from the response
    const updatedPost = response.data;

     // Ensure that updatedPost contains the new `favoritedBy` array
     console.log('Updated Post:', updatedPost);

    // Update the post in the local posts state with the new favoritedBy data
    const updatedPosts = posts.map(post =>
      post._id === postId ? { ...post, favoritedBy: updatedPost.favoritedBy } : post
    );
    setPosts(updatedPosts);

    // Return the updated post data to the calling function
    return updatedPost;

  } catch (error) {
    console.error('Error toggling favorite:', error);
    throw error;
  }
};


  // Add a comment
  const addComment = async (postId, commentData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_END_POINT}/blog/post/${postId}/comment`, commentData);
      const updatedPost = posts.map(post =>
        post._id === postId ? { ...post, comments: [...post.comments, response.data] } : post
      );
      setPosts(updatedPost);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Add a reply to a comment
  const addReply = async (postId, commentId, replyData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_END_POINT}/blog/post/${postId}/comment/${commentId}/reply`, replyData);
      const updatedPost = posts.map(post => {
        if (post._id === postId) {
          const updatedComments = post.comments.map(comment =>
            comment._id === commentId ? { ...comment, replies: [...comment.replies, response.data] } : comment
          );
          return { ...post, comments: updatedComments };
        }
        return post;
      });
      setPosts(updatedPost);
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  // Like/Unlike a comment
  const toggleLikeComment = async (postId, commentId) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_END_POINT}/blog/post/${postId}/comment/${commentId}/like`);
      const updatedPost = posts.map(post => {
        if (post._id === postId) {
          const updatedComments = post.comments.map(comment =>
            comment._id === commentId ? { ...comment, likes: response.data.likes } : comment
          );
          return { ...post, comments: updatedComments };
        }
        return post;
      });
      setPosts(updatedPost);
    } catch (error) {
      console.error('Error liking/unliking comment:', error);
    }
  };

  // Fetch posts on mount
  useEffect(() => {
    fetchPosts();
  }, [userInfo]);

  return (
    <DBPostsContext.Provider
      value={{
        posts,
        isLoading,
        fetchPosts,
        addPost,
        updatePost,
        deletePost,
        toggleFavoritePost,
        addComment,
        addReply,
        toggleLikeComment,
      }}
    >
      {children}
    </DBPostsContext.Provider>
  );
};
